import React from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import { autobind } from 'core-decorators';
import { EventEmitter } from 'fbemitter';
import { FlexColContainer } from 'components/StyledComp';
import LoadingIndicator from 'components/LoadingIndicator';
import HttpClient from 'network/HttpClient';
import ResponseHandler from 'network/ResponseHandler';
import dataStorage from 'misc/data-storage';
import platforms from 'misc/platforms';
import constants from 'misc/constants';
import helpers from 'misc/helpers';
import enums from 'misc/enums';
import responseErrors from 'misc/response-errors';
import PageContext from './PageContext';

const propTypes = {
  location: PropTypes.object.isRequired,
  navigate: PropTypes.object.isRequired,
};

@autobind
class BasePage extends React.PureComponent {
  constructor(props) {
    super(props);

    const responseHandler = new ResponseHandler(this, responseErrors);
    this.httpClient = new HttpClient(constants.URL_WEB_SERVICE, responseHandler);
    this.eventEmitter = new EventEmitter();
  }

  // eslint-disable-next-line
  UNSAFE_componentWillMount() {
    this.setTitle(this.getTitle() ? `${this.getTitle()} - HHAPlus` : 'HHAPlus');
  }

  getTitle() {
    return '';
  }

  /**
   * Sets the page title dynamically.
   */
  setTitle(title) {
    document.title = title;
  }

  getMyUser() {
    return dataStorage.getCurrentUser();
  }

  getMyUserId() {
    return dataStorage.getUserId();
  }

  getMyNickName() {
    return dataStorage.getNickName();
  }

  getMyUserType() {
    return dataStorage.getUserType();
  }

  isMeTeamAgency() {
    return dataStorage.isTeamAgency();
  }

  isMeRealAgency() {
    return !dataStorage.isTeamAgency();
  }

  isLoggedIn() {
    return dataStorage.hasCredential();
  }

  parseQueryParam(paramName, location = this.props.location) {
    return queryString.parse(location.search)[paramName];
  }

  navigateTo(dest) {
    this.props.navigate(dest);
  }

  emitEvent(eventName, ...args) {
    this.eventEmitter.emit(eventName, ...args);
  }

  addEventListener(eventName, listener) {
    return this.eventEmitter.addListener(eventName, listener);
  }

  async callService(relUrl, ...args) {
    return this.httpClient.callService(relUrl, ...args);
  }

  async callServiceForRemoteObject(relUrl, ...args) {
    return this.httpClient.callServiceForRemoteObject(relUrl, ...args);
  }

  async callServiceForPagedObjects(relUrl, ...args) {
    return this.httpClient.callServiceForPagedObjects(relUrl, ...args);
  }

  async callServiceForRemoteObjects(relUrl, ...args) {
    return this.httpClient.callServiceForRemoteObjects(relUrl, ...args);
  }

  async callServiceForObject(relUrl, ...args) {
    return this.httpClient.callServiceForObject(relUrl, ...args);
  }

  async callServiceForObjects(relUrl, ...args) {
    return this.httpClient.callServiceForObjects(relUrl, ...args);
  }

  async callServiceForString(relUrl, ...args) {
    return this.httpClient.callServiceForString(relUrl, ...args);
  }

  async callServiceForLong(relUrl, ...args) {
    return this.httpClient.callServiceForLong(relUrl, ...args);
  }

  async callServiceForBoolean(relUrl, ...args) {
    return this.httpClient.callServiceForBoolean(relUrl, ...args);
  }

  notifySuccess(title, content) {
    helpers.showNotification('success', title, content);
  }

  notifyWarning(title, content) {
    helpers.showNotification('warning', title, content);
  }

  notifyError(title, content) {
    helpers.showNotification('failure', title, content);
  }

  isInAppWebview() {
    return platforms.isInAppWebview();
  }

  isInRniOSWebView() {
    return platforms.isInRniOSWebView();
  }

  isInRnAndroidWebView() {
    return platforms.isInRnAndroidWebView();
  }

  isInRenderableState() {
    return true;
  }

  e2t(type, enumValue) {
    return enums.toText(type, enumValue);
  }

  renderLoadingComponent() {
    return (
      <FlexColContainer
        width="match_parent"
        height="match_parent"
        minHeight="100vh"
        justifyContent="center"
        alignItems="center"
      >
        <LoadingIndicator
          type="ThreeBounce"
          size={32}
        />
      </FlexColContainer>
    );
  }

  renderContent() {
    throw new Error('You have to implement the method!');
  }

  render() {
    const renderable = this.isInRenderableState();

    return (
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      <PageContext.Provider value={{
        location: this.props.location,
        navigate: this.props.navigate,
        eventEmitter: this.eventEmitter,
        basePage: this,
      }}
      >
        {renderable && this.renderContent()}
        {!renderable && this.renderLoadingComponent()}
      </PageContext.Provider>
    );
  }
}

// https://facebook.github.io/react/docs/typechecking-with-proptypes.html
BasePage.propTypes = propTypes;

export default BasePage;
